/**
 * The demo's additions to the console page.
 *
 * ⚠️ DEMO ONLY. The page itself is src/web/page.ts, unchanged. In `sibyl web`, Review and Post
 * point at the command line, because the console does not start reviews or post. Here they run the
 * same commands in the browser and show what they did, so every control on the page does something.
 */

import * as R from "./runtime"

declare global {
  interface Window {
    __sibylReload?: () => Promise<void>
    render?: () => void
    post?: (p: Record<string, string>) => Promise<boolean>
    visible?: () => Array<{ fingerprint: string; severity: string; state?: { kind: string } }>
  }
}

const $ = (id: string) => document.getElementById(id) as HTMLElement
const esc = (s: unknown) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")

const STYLE = `
.demobar{display:flex;align-items:center;gap:9px;flex-wrap:wrap;padding:8px 20px;font-size:12px;
  border-bottom:1px solid var(--color-line);background:color-mix(in oklch,var(--color-accent) 9%,transparent)}
.demobar b{color:var(--color-accent)}
.demobar .grow{flex:1;min-width:200px;color:var(--color-muted);line-height:1.5}
.demobar button{padding:4px 10px;font-size:12px}
.modal-back{position:fixed;inset:0;z-index:50;background:rgba(0,0,0,.55);display:flex;align-items:flex-start;
  justify-content:center;padding:4vh 12px;overflow:auto}
.modal{width:min(980px,100%);background:var(--surface-solid);color:var(--color-ink);border:1px solid var(--color-line);
  border-radius:var(--radius);box-shadow:0 30px 60px -20px rgba(0,0,0,.6);display:flex;flex-direction:column;max-height:92vh}
.modal header{border-bottom:1px solid var(--color-line);padding:12px 16px;display:flex;align-items:center;gap:10px}
.modal header h1{font-size:14px}
.modal header .x{margin-left:auto}
.modal .mbody{padding:14px 16px;overflow:auto;display:flex;flex-direction:column;gap:12px}
.modal .mbody>*{flex-shrink:0}
.term{margin:0;padding:12px 14px;background:oklch(0.14 0.01 264);color:oklch(0.93 0.01 90);border-radius:8px;
  font:12px/1.55 ui-monospace,SFMono-Regular,Menlo,monospace;white-space:pre-wrap;word-break:break-word;min-height:120px;
  max-height:52vh;overflow:auto;border:1px solid var(--color-line)}
.term .e{color:oklch(0.80 0.06 250)} .term .c{color:oklch(0.85 0.14 150)} .term .x{color:oklch(0.75 0.17 25)}
.chips{display:flex;flex-wrap:wrap;gap:7px}
.chip{display:inline-flex;align-items:center;gap:6px;padding:4px 9px;border-radius:999px;font-size:12px;
  border:1px solid var(--color-line);color:var(--color-muted)}
.chip.run{border-color:var(--color-accent);color:var(--color-accent)}
.chip.ok{border-color:var(--color-ok);color:var(--color-ok);background:transparent}
.chip.off{opacity:.45}
.spin{width:10px;height:10px;border-radius:50%;border:2px solid currentColor;border-right-color:transparent;animation:sp .8s linear infinite}
@keyframes sp{to{transform:rotate(360deg)}}
.cmdline{display:flex;gap:8px;align-items:center}
.cmdline span{font:13px ui-monospace,monospace;color:var(--color-muted)}
.quick{display:flex;flex-wrap:wrap;gap:6px}
.quick button{padding:4px 9px;font:12px ui-monospace,monospace}
.gh{border:1px solid var(--color-line);border-radius:8px;overflow:hidden}
.gh .ghh{padding:9px 12px;background:color-mix(in oklch,var(--color-ink) 6%,transparent);font-size:12.5px;display:flex;gap:8px;align-items:center;flex-wrap:wrap}
.gh .ghb{padding:10px 12px;font-size:13px;line-height:1.6}
.gh .ghb p{margin:0 0 8px}
.gh .file{font:12px ui-monospace,monospace;color:var(--color-muted);margin-bottom:6px}
.gh code{font:12px ui-monospace,monospace;background:color-mix(in oklch,var(--color-ink) 8%,transparent);padding:1px 4px;border-radius:4px}
.gh pre{margin:6px 0}
.avatar{width:22px;height:22px;border-radius:50%;background:var(--color-accent);display:inline-block}
.credin{display:flex;gap:6px;margin:4px 0 8px}
.credin input{flex:1;padding:5px 8px;font-size:12px}
.credin button{padding:4px 9px;font-size:12px}
.toast{position:fixed;left:50%;bottom:22px;transform:translateX(-50%);z-index:60;background:var(--surface-solid);
  border:1px solid var(--color-accent);color:var(--color-ink);border-radius:10px;padding:10px 14px;font-size:13px;
  box-shadow:0 20px 40px -18px rgba(0,0,0,.6);max-width:min(560px,92vw)}
.note2{font-size:12px;color:var(--color-muted);line-height:1.55;margin:0}
.row2{display:flex;gap:8px;flex-wrap:wrap;align-items:center}
.row2 select{width:auto}
`

// ── Small pieces ───────────────────────────────────────────────────────────────────────────────

function modal(title: string, onClose?: () => void): { body: HTMLElement; close: () => void; head: HTMLElement } {
  const back = document.createElement("div")
  back.className = "modal-back"
  back.innerHTML = `<div class="modal" role="dialog" aria-modal="true" aria-label="${esc(title)}"><header><h1>${esc(title)}</h1><button class="x" data-close>Close</button></header><div class="mbody"></div></div>`
  document.body.appendChild(back)
  const close = () => {
    back.remove()
    document.removeEventListener("keydown", onKey)
    onClose?.()
  }
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") close()
  }
  document.addEventListener("keydown", onKey)
  back.addEventListener("click", (e) => {
    if (e.target === back) close()
  })
  ;(back.querySelector("[data-close]") as HTMLElement).onclick = close
  return { body: back.querySelector(".mbody") as HTMLElement, close, head: back.querySelector("header") as HTMLElement }
}

function toast(html: string, ms = 5200): void {
  const t = document.createElement("div")
  t.className = "toast"
  t.setAttribute("role", "status")
  t.innerHTML = html
  document.body.appendChild(t)
  setTimeout(() => t.remove(), ms)
}

function termWriter(pre: HTMLElement): R.Term {
  const add = (s: string, cls: string) => {
    const span = document.createElement("span")
    if (cls !== "") span.className = cls
    span.textContent = s
    pre.appendChild(span)
    pre.scrollTop = pre.scrollHeight
  }
  return { out: (s) => add(s, ""), err: (s) => add(s, s.startsWith("⛔") ? "x" : "e") }
}

async function reload(): Promise<void> {
  await window.__sibylReload?.()
  augmentCredentials()
  refreshBar()
}

// ── The demo bar ───────────────────────────────────────────────────────────────────────────────

function installBar(): void {
  const bar = document.createElement("div")
  bar.className = "demobar"
  bar.id = "demobar"
  bar.innerHTML = `<span class="grow"><b>Live demo.</b> sibyl is running in your browser against a sample pull request,
    <code>${esc(R.scenario.REPO)}#${R.scenario.PR_NUMBER}</code>. The model's answers are recorded and GitHub is simulated;
    the gates, fingerprints, triage store and posting are sibyl's own code. Nothing leaves this page.</span>
    <button id="d-term">Terminal</button>
    <button id="d-gh">Pull request on GitHub</button>
    <button id="d-push">Author pushes a fix</button>
    <button id="d-reset">Reset demo</button>`
  document.querySelector("header")?.after(bar)
  // The page's layout height assumes only the header sits above it.
  const fit = () => {
    const h = (document.querySelector("header") as HTMLElement).offsetHeight + bar.offsetHeight
    ;(document.querySelector(".layout") as HTMLElement).style.height = window.innerWidth > 900 ? `calc(100vh - ${h}px)` : ""
  }
  fit()
  window.addEventListener("resize", fit)
  $("d-term").onclick = () => openTerminal()
  $("d-gh").onclick = () => openGitHub()
  $("d-push").onclick = () => authorPushes()
  $("d-reset").onclick = async () => {
    $("d-reset").textContent = "Resetting…"
    await R.reset()
    $("d-reset").textContent = "Reset demo"
    await reload()
    toast("Demo reset: the first push of the pull request has been reviewed again and the queue is back to its starting state.")
  }
  const pill = Array.from(document.querySelectorAll("header .pill")).find((p) => p.textContent === "127.0.0.1 only") as HTMLElement | undefined
  if (pill !== undefined) {
    pill.textContent = "Live demo — runs in your browser"
    pill.className = "pill acc"
    pill.title = "sibyl web binds 127.0.0.1 only; this demo has no server at all"
  }
}

function refreshBar(): void {
  const s = R.getState()
  const push = $("d-push") as HTMLButtonElement
  push.disabled = s.head === "v2"
  push.textContent = s.head === "v2" ? "Author has pushed a fix" : "Author pushes a fix"
  const n = s.github.filter((e) => e.kind === "review").length
  $("d-gh").textContent = n > 0 ? `Pull request on GitHub (${n} review${n === 1 ? "" : "s"})` : "Pull request on GitHub"
}

function authorPushes(): void {
  R.authorPushes()
  refreshBar()
  const paths = R.pushedPaths()
  toast(
    `<b>${esc(R.scenario.AUTHOR)}</b> pushed to <code>#${R.scenario.PR_NUMBER}</code>, changing ${paths.map((p) => `<code>${esc(p)}</code>`).join(", ")}. ` +
      `Run <b>Review</b> to review the new head, or <code>sibyl recheck ${R.scenario.PR_NUMBER}</code> in the terminal to see which accepted findings it cleared.`,
    9000,
  )
}

// ── Review ─────────────────────────────────────────────────────────────────────────────────────

async function dimensionChoices(): Promise<{ ids: string[]; on: Record<string, boolean> }> {
  const s = (await (await fetch("/api/state")).json()) as { dimensions: Array<{ id: string; title: string }> }
  const togs = Array.from(document.querySelectorAll("#dims .tog"))
  const on: Record<string, boolean> = {}
  s.dimensions.forEach((d, i) => (on[d.id] = togs[i]?.getAttribute("aria-checked") === "true"))
  return { ids: s.dimensions.map((d) => d.id), on }
}

async function runReview(): Promise<void> {
  const pr = Number(($("pr") as HTMLInputElement).value.trim())
  const repo = ($("repo") as HTMLInputElement).value.trim() || R.scenario.REPO
  const backend = ($("backend") as HTMLSelectElement).value as "claude_cli" | "anthropic_api"
  const ref = ($("ref") as HTMLSelectElement).selectedIndex === 1 ? "head" : "merge"
  const dims = await dimensionChoices()
  const m = modal(`sibyl review ${Number.isFinite(pr) ? pr : "?"}`)
  m.body.innerHTML = `<p class="note2">The real review pipeline, running now: read the pull request, build the anchor set from its own patches,
    ask each enabled dimension, check every claim against the checked-out tree, send the survivors to the refuter, and write what is left to the triage queue.
    The model's answers are recorded for this sample; everything that checks them is live.</p>
    <div class="chips" id="rv-chips">${dims.ids
      .map((id) => `<span class="chip ${dims.on[id] ? "" : "off"}" data-dim="${esc(id)}">${esc(id.replace(/_/g, " "))}${dims.on[id] ? "" : " · off"}</span>`)
      .join("")}</div>
    <pre class="term" id="rv-term"><span class="c">$ sibyl review ${esc(String(pr))}${repo !== R.scenario.REPO ? ` --repo ${esc(repo)}` : ""}</span>\n</pre>
    <div class="row2" id="rv-done" hidden><button class="okb" id="rv-close">Show the queue</button><span class="note2" id="rv-sum"></span></div>`
  const pre = m.body.querySelector("#rv-term") as HTMLElement
  const chip = (id: string) => m.body.querySelector(`[data-dim="${CSS_ESCAPE(id)}"]`) as HTMLElement | null
  const rc = await R.runReview(termWriter(pre), {
    pr,
    repo,
    backend,
    ref,
    dimensions: dims.on,
    pace: 1,
    onEvent: (e) => {
      const c = chip(e.dimension)
      if (c === null) return
      if (e.kind === "start") {
        c.className = "chip run"
        c.innerHTML = `<span class="spin"></span>${esc(e.dimension.replace(/_/g, " "))}${e.refute ? " · refuting" : " · reviewing"}`
      } else {
        c.className = "chip ok"
        c.textContent = `${e.dimension.replace(/_/g, " ")} ✓ ${e.refute ? `refuter: ${e.verdict}` : `${e.findings} claim${e.findings === 1 ? "" : "s"}`}`
      }
    },
  })
  pre.insertAdjacentHTML("beforeend", `\n<span class="${rc === 0 ? "c" : "x"}">exit ${rc}</span>\n`)
  await reload()
  ;($("rv-done") as HTMLElement).hidden = false
  ;($("rv-sum") as HTMLElement).textContent = rc === 0 ? "The queue behind this window now shows the result." : "The review did not run; the message above says why."
  ;($("rv-close") as HTMLElement).onclick = m.close
}
const CSS_ESCAPE = (s: string) => (typeof window.CSS?.escape === "function" ? window.CSS.escape(s) : s)

// ── Post ───────────────────────────────────────────────────────────────────────────────────────

async function runPost(): Promise<void> {
  const m = modal(`sibyl post ${R.scenario.PR_NUMBER}`)
  const st = (await (await fetch("/api/state")).json()) as {
    queue: { counts: { todo: number; accepted: number }; findings: Array<{ fingerprint: string; title: string; state: { kind: string } }> }
    dismissReasons: string[]
  }
  const undecided = st.queue.findings.filter((f) => f.state.kind === "todo")
  if (undecided.length > 0) {
    // ⛔ sibyl's human gate: nothing posts while a finding in the touched files is undecided — the
    // real command refuses and names them. The demo asks for those decisions here instead of running
    // into the refusal.
    m.body.innerHTML = `<p class="note2">sibyl posts only when <b>every</b> finding has a decision — <code>triage.require_human</code>, its master switch.
      ${st.queue.counts.accepted} accepted; these ${undecided.length} are still undecided:</p>
      <ul class="note2" style="margin:0;padding-left:18px">${undecided.map((f) => `<li>${esc(f.title)}</li>`).join("")}</ul>
      <div class="row2"><select id="po-r">${st.dismissReasons.map((r) => `<option value="${esc(r)}"${r === "wont_fix" ? " selected" : ""}>${esc(r.replace(/_/g, " "))}</option>`).join("")}</select>
      <button class="badb" id="po-decide">Dismiss these ${undecided.length} and post the ${st.queue.counts.accepted} accepted</button>
      <button id="po-back">Back to the queue</button></div>`
    ;(m.body.querySelector("#po-back") as HTMLElement).onclick = m.close
    ;(m.body.querySelector("#po-decide") as HTMLElement).onclick = async () => {
      const reason = (m.body.querySelector("#po-r") as HTMLSelectElement).value
      for (const f of undecided) await window.post!({ fingerprint: f.fingerprint, action: "dismiss", reason, note: "decided before posting" })
      await postNow(m)
    }
    return
  }
  await postNow(m)
}

async function postNow(m: { body: HTMLElement }): Promise<void> {
  const before = R.getState().github.length
  m.body.innerHTML = `<p class="note2">The real posting command: it re-reads the pull request, rebuilds where a comment may legally go,
    and posts the accepted findings as ONE review plus a commit status — here, to a simulated GitHub.</p>
    <pre class="term" id="po-term"><span class="c">$ sibyl post ${R.scenario.PR_NUMBER}</span>\n</pre><div id="po-gh"></div>`
  const pre = m.body.querySelector("#po-term") as HTMLElement
  const rc = await R.runPost(termWriter(pre), [String(R.scenario.PR_NUMBER)])
  pre.insertAdjacentHTML("beforeend", `\n<span class="${rc === 0 ? "c" : "x"}">exit ${rc}</span>\n`)
  const fresh = R.getState().github.slice(before)
  const ghBox = m.body.querySelector("#po-gh") as HTMLElement
  ghBox.innerHTML = fresh.length
    ? `<h2 style="margin:4px 0 8px">What GitHub received</h2>${renderGitHub(fresh)}`
    : `<p class="note2">Nothing was sent to GitHub — the output above says why.</p>`
  pre.scrollTop = 0
  await reload()
}

// ── Simulated GitHub ───────────────────────────────────────────────────────────────────────────

function md(s: string): string {
  // Just enough Markdown for what sibyl writes: bold, code, fenced blocks, <details>, paragraphs.
  const blocks = s.split(/\n{2,}/)
  return blocks
    .map((b) => {
      if (b.startsWith("```")) return `<pre>${esc(b.replace(/^```\w*\n?/, "").replace(/\n?```$/, ""))}</pre>`
      if (b.startsWith("<!--")) return ""
      if (b.startsWith("<details>")) {
        const inner = b.replace(/<\/?details>|<summary>|<\/summary>/g, "")
        return `<details><summary>${esc(b.match(/<summary>(.*?)<\/summary>/)?.[1] ?? "details")}</summary><p>${esc(inner.replace(b.match(/<summary>(.*?)<\/summary>/)?.[1] ?? "", "")).replace(/\n/g, "<br>")}</p></details>`
      }
      return `<p>${esc(b)
        .replace(/\*\*(.+?)\*\*/g, "<b>$1</b>")
        .replace(/`([^`]+)`/g, "<code>$1</code>")
        .replace(/\n/g, "<br>")}</p>`
    })
    .join("")
}

function renderGitHub(events: R.GitHubEvent[]): string {
  return events
    .map((e) => {
      if (e.kind === "label") {
        return `<div class="gh"><div class="ghh"><span class="pill acc">label</span> <b>sibyl</b> added — the workflow took it off again as the run started, so adding it again asks for a re-review</div></div>`
      }
      if (e.kind === "status") {
        const st = String(e.body["state"])
        return `<div class="gh"><div class="ghh"><span class="pill ${st === "success" ? "ok" : st === "failure" ? "bad" : "warn"}">${esc(st)}</span>
          commit status <code>${esc(e.body["context"])}</code> on <code>${esc(String(e.body["sha"]).slice(0, 7))}</code> — ${esc(e.body["description"])}</div></div>`
      }
      const comments = (e.body["comments"] as Array<Record<string, unknown>> | undefined) ?? []
      return `<div class="gh"><div class="ghh"><span class="avatar"></span><b>sibyl</b> reviewed · <span class="pill mut">${esc(e.body["event"])}</span>
        on <code>${esc(String(e.body["commit_id"]).slice(0, 7))}</code> · ${comments.length} inline comment${comments.length === 1 ? "" : "s"}</div>
        <div class="ghb">${md(String(e.body["body"] ?? ""))}</div>
        ${comments
          .map(
            (c) => `<div class="ghb" style="border-top:1px solid var(--color-line)"><div class="file">${esc(c["path"])}:${esc(c["line"])}</div>${md(String(c["body"] ?? ""))}</div>`,
          )
          .join("")}</div>`
    })
    .join("")
}

function checkLine(): string {
  const c = R.checks()["sibyl"]
  if (c === undefined) return `<span class="pill mut">expected</span> <b>sibyl</b> — required, waiting for status to be reported`
  const st = String(c["state"])
  const tone = st === "success" ? "ok" : st === "pending" ? "warn" : "bad"
  return `<span class="pill ${tone}">${esc(st)}</span> <b>sibyl</b> — ${esc(c["description"])}`
}

function openGitHub(): void {
  const m = modal(`${R.scenario.REPO} #${R.scenario.PR_NUMBER} — simulated GitHub`)
  const draw = () => {
    const s = R.getState()
    const head = R.currentHead()
    const ok = R.checks()["sibyl"]?.["state"] === "success"
    m.body.innerHTML = `<div class="gh"><div class="ghh"><span class="pill ok">open</span><b>${esc(R.scenario.PR_TITLE)}</b>
        <span class="note2">#${R.scenario.PR_NUMBER} · ${esc(R.scenario.AUTHOR)} wants to merge into <code>main</code> · head <code>${head.slice(0, 7)}</code>
        ${s.head === "v2" ? " · the author has pushed a fix since the first review" : ""}</span></div>
        <div class="ghb"><div class="row2" style="margin-bottom:8px">${checkLine()}</div>
        <div class="row2"><button class="okb" id="gh-merge" ${ok ? "" : "disabled"}>Merge pull request</button>
        <span class="note2">${ok ? "The required check has passed." : "Merging is blocked: the required check <b>sibyl</b> has not passed."}</span></div>
        <div class="row2" style="margin-top:10px"><button id="gh-label">Add the <b>sibyl</b> label</button>
        <span class="note2">This is how a review is asked for on GitHub: the shipped workflow (templates/sibyl-review.yml) runs <code>sibyl review</code> and <code>sibyl post</code> and sets the check.</span></div>
        <pre class="term" id="gh-run" hidden></pre></div></div>
      <h2 style="margin:6px 0 0">Timeline</h2>
      ${s.github.length ? renderGitHub([...s.github].reverse()) : `<p class="note2">Nothing yet. Add the label, or accept findings in the console and press <b>Post accepted…</b>.</p>`}`
    ;(m.body.querySelector("#gh-merge") as HTMLElement).onclick = () =>
      toast("Merged — in a real repository the branch would now be in <code>main</code>. (Simulated: the demo's pull request stays open so you can keep trying it.)")
    ;(m.body.querySelector("#gh-label") as HTMLElement).onclick = async () => {
      const pre = m.body.querySelector("#gh-run") as HTMLElement
      pre.hidden = false
      pre.textContent = ""
      ;(m.body.querySelector("#gh-label") as HTMLButtonElement).disabled = true
      pre.insertAdjacentHTML("beforeend", `<span class="c">GitHub Actions · workflow "sibyl" · pull_request_target: labeled</span>\n`)
      const rc = await R.runLabelWorkflow(termWriter(pre))
      pre.insertAdjacentHTML("beforeend", `\n<span class="${rc === 0 ? "c" : "x"}">workflow ${rc === 0 ? "succeeded" : "failed"}</span>\n`)
      await reload()
      const log = pre.innerHTML
      draw()
      const p2 = m.body.querySelector("#gh-run") as HTMLElement
      p2.hidden = false
      p2.innerHTML = log
    }
  }
  draw()
}

// ── Terminal ───────────────────────────────────────────────────────────────────────────────────

const QUICK = [
  "--help",
  "triage",
  "triage --all",
  `review ${R.scenario.PR_NUMBER}`,
  `post ${R.scenario.PR_NUMBER} --dry-run`,
  `post ${R.scenario.PR_NUMBER}`,
  `recheck ${R.scenario.PR_NUMBER}`,
  "config check",
  "config print",
  "init",
  "web",
]

function openTerminal(prefill = ""): void {
  const m = modal("Terminal — the sibyl command line")
  m.body.innerHTML = `<p class="note2">Every sibyl command, run by sibyl's own command-line entry point against the same queue the console shows.
    Try <code>triage accept &lt;fingerprint&gt;</code> or <code>triage dismiss &lt;fingerprint&gt; --reason wont_fix</code> with a fingerprint from <code>triage</code>.</p>
    <div class="quick">${QUICK.map((q) => `<button data-q="${esc(q)}">sibyl ${esc(q)}</button>`).join("")}</div>
    <pre class="term" id="tm-out"></pre>
    <form class="cmdline" id="tm-form"><span>$ sibyl</span><input id="tm-in" autocomplete="off" spellcheck="false" placeholder="triage --all"/><button>Run</button></form>`
  const pre = m.body.querySelector("#tm-out") as HTMLElement
  const input = m.body.querySelector("#tm-in") as HTMLInputElement
  input.value = prefill
  let chain: Promise<void> = Promise.resolve()
  const run = (line: string): Promise<void> => (chain = chain.then(() => runNow(line)))
  const runNow = async (line: string) => {
    const argv = line.trim().split(/\s+/).filter(Boolean)
    pre.insertAdjacentHTML("beforeend", `<span class="c">$ sibyl ${esc(argv.join(" "))}</span>\n`)
    const rc = argv.length === 0 ? await R.runCli(termWriter(pre), ["--help"]) : await R.runCli(termWriter(pre), argv)
    pre.insertAdjacentHTML("beforeend", `<span class="${rc === 0 ? "c" : "x"}">exit ${rc}</span>\n\n`)
    pre.scrollTop = pre.scrollHeight
    await reload()
  }
  ;(m.body.querySelector("#tm-form") as HTMLFormElement).onsubmit = (e) => {
    e.preventDefault()
    const v = input.value
    input.value = ""
    void run(v)
  }
  m.body.querySelectorAll<HTMLElement>("[data-q]").forEach((b) => (b.onclick = () => void run(b.dataset["q"] ?? "")))
  input.focus()
  if (prefill === "") void run("--help")
}

// ── Dismiss all shown ──────────────────────────────────────────────────────────────────────────

async function dismissShown(): Promise<void> {
  const shown = (window.visible?.() ?? []).filter((f) => (f.state?.kind ?? "todo") === "todo")
  const s = (await (await fetch("/api/state")).json()) as { dismissReasons: string[] }
  const m = modal(`Dismiss ${shown.length} shown finding${shown.length === 1 ? "" : "s"}`)
  if (shown.length === 0) {
    m.body.innerHTML = `<p class="note2">Every finding shown has already been decided. Turn off <b>hide decided</b> or change the filters to see more.</p>`
    return
  }
  m.body.innerHTML = `<p class="note2">One reason for the batch, chosen before anything is written. Each finding still gets its own dated record.</p>
    <div class="row2"><select id="ds-r"><option value="" selected disabled>choose a reason…</option>${s.dismissReasons
      .map((r) => `<option value="${esc(r)}">${esc(r.replace(/_/g, " "))}</option>`)
      .join("")}</select><input id="ds-n" placeholder="note (optional)" style="flex:1;min-width:160px"/>
    <button class="badb" id="ds-go">Dismiss ${shown.length}</button></div><p class="note2" id="ds-msg"></p>`
  ;(m.body.querySelector("#ds-go") as HTMLElement).onclick = async () => {
    const reason = (m.body.querySelector("#ds-r") as HTMLSelectElement).value
    const note = (m.body.querySelector("#ds-n") as HTMLInputElement).value
    if (reason === "") {
      ;(m.body.querySelector("#ds-msg") as HTMLElement).textContent = "Pick a reason first — a dismissal with no reason is refused."
      return
    }
    for (const f of shown) {
      if (!(await window.post!({ fingerprint: f.fingerprint, action: "dismiss", reason, note }))) break
    }
    m.close()
    toast(`Dismissed ${shown.length} finding${shown.length === 1 ? "" : "s"} as <b>${esc(reason.replace(/_/g, " "))}</b>.`)
  }
}

// ── Credentials, for this browser tab only ─────────────────────────────────────────────────────

function augmentCredentials(): void {
  const box = document.getElementById("creds")
  if (box === null) return
  box.querySelectorAll(".dim").forEach((row) => {
    if (row.nextElementSibling?.classList.contains("credin")) return
    const name = row.querySelector("span")?.textContent ?? ""
    const form = document.createElement("form")
    form.className = "credin"
    form.innerHTML = `<input type="password" autocomplete="off" placeholder="type to set — this tab only" aria-label="${esc(name)}"/><button>Save</button>`
    form.onsubmit = async (e) => {
      e.preventDefault()
      const v = (form.querySelector("input") as HTMLInputElement).value
      await R.setCredential(name, v)
      await reload()
      toast(v === "" ? `${esc(name)} cleared.` : `${esc(name)} saved for this browser tab — the console shows its presence and last four characters, never the value.`)
    }
    row.after(form)
  })
}

// ── Wiring ─────────────────────────────────────────────────────────────────────────────────────

function wire(): void {
  // ⛔ In `sibyl web` these two buttons explain that reviews start and posts happen at the command
  // line. In the demo they run those commands, so a visitor sees what each one does.
  $("run").onclick = () => void runReview()
  $("post").onclick = () => void runPost()
  $("dismissshown").onclick = () => void dismissShown()
}

export async function startUi(): Promise<void> {
  const style = document.createElement("style")
  style.textContent = STYLE
  document.head.appendChild(style)
  installBar()
  wire()
  // Wait for the page's own first render, then add the credential inputs it does not have.
  for (let i = 0; i < 200 && document.querySelector("#creds .dim") === null; i++) await new Promise((r) => setTimeout(r, 25))
  augmentCredentials()
  refreshBar()
}
